import katex from 'katex';
import 'katex/dist/katex.min.css';
import { useMemo, useRef } from 'react';
import type { LatexContent, ScriptElement } from '../../schema';
import { useLatexWriteOn } from './latexWriteOn';
import { texToKalam } from './texToKalam';
import { TextElement } from './TextElement';

/**
 * HYBRID formula rendering (leadership call 2026-08-09): a SIMPLE formula is
 * flattened to inline Unicode and drawn as real handwriting — it write-on
 * animates with the teacher's voice exactly like his board text, instead of
 * popping in as a typeset block. Anything the flattener can't represent
 * faithfully (integrals, sums, matrices, \vec, complex exponents, unknown
 * commands) stays real KaTeX, so calculus/integration content is never
 * silently mangled. Flip LTT_FORMULA_MODE / this flag to force one path.
 */
const FLATTEN_FORMULAS = true;

function asLatex(content: unknown): LatexContent {
  if (typeof content === 'string') return { source: content };
  if (content && typeof content === 'object') {
    const c = content as LatexContent & { tex?: string };
    // Tolerate malformed/legacy content (e.g. `tex` instead of `source`, or a
    // missing source) — a bad element must never crash the whole slide.
    return { ...c, source: typeof c.source === 'string' ? c.source : (typeof c.tex === 'string' ? c.tex : '') };
  }
  return { source: '' };
}

/**
 * KaTeX rendered with chalk-cream color so equations read on the dark board.
 * `displayMode` honored from the authored content.
 */
export function LatexElement({
  element,
  fill,
  writing,
  writeMs,
  onWritingDone,
  paused,
}: {
  element: ScriptElement;
  fill?: boolean;
  /** Write the equation on glyph-by-glyph with a pencil (handwritten style). */
  writing?: boolean;
  /** Target duration (ms) for the write-on, synced to the teacher's pace. */
  writeMs?: number;
  /** Fired when the glyph stream finishes. */
  onWritingDone?: () => void;
  /** Freeze the glyph stream in place (mirrors the player's pause). */
  paused?: boolean;
}) {
  const data = asLatex(element.content);
  const fontSize = (element.style?.fontSize as number | undefined) ?? 22;
  const color = (element.style?.color as string | undefined) ?? '#2f2a22';
  const hostRef = useRef<HTMLDivElement | null>(null);

  // Flatten path: hand the tex to the ordinary handwriting engine as a synthetic
  // text element (same ChalkWriter/pencil write-on, font loading, accents). Only
  // when the flattener reports it's faithful — otherwise fall through to KaTeX.
  // NOTE: every hook below runs unconditionally (React rules-of-hooks); the
  // flatten/KaTeX branch is decided only at the render return.
  const flat = useMemo(
    () => (FLATTEN_FORMULAS ? texToKalam(data.source) : { text: '', lossy: true }),
    [data.source],
  );
  const useFlat = FLATTEN_FORMULAS && !flat.lossy && !!flat.text;
  const ta = element.style?.textAlign as 'left' | 'center' | 'right' | undefined;
  const justify = ta === 'center' ? 'center' : ta === 'right' ? 'flex-end' : 'flex-start';

  const html = useMemo(() => {
    if (!data.source.trim()) return null;
    try {
      return katex.renderToString(data.source, {
        displayMode: data.displayMode ?? false,
        throwOnError: false,
        output: 'html',
        strict: 'ignore',
      });
    } catch (err) {
      return `<span style="color:#ff8a80">${(err as Error).message}</span>`;
    }
  }, [data.source, data.displayMode]);

  useLatexWriteOn(hostRef, {
    // In flatten mode hostRef is never mounted (we return a TextElement), so the
    // hook no-ops; disable it explicitly so it can't hide a would-be render.
    writing: !useFlat && !!writing,
    paused,
    fontSize,
    html,
    onDone: onWritingDone,
    durationMs: writeMs,
  });

  if (useFlat) {
    // texToKalam returns RAW text; escape it for the <span> (a bare "a < b"
    // would otherwise be parsed as a tag by the handwriting HTML pipeline).
    const escaped = flat.text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const textEl = {
      ...element,
      type: 'text',
      content: `<span>${escaped}</span>`,
      handwritten: true,
      style: {
        ...(element.style as Record<string, unknown> | undefined),
        fontFamily: undefined, // → board chalk font, identical engine to his text
        fontWeight: (element.style?.fontWeight as number | undefined) ?? 600,
      },
    } as unknown as ScriptElement;
    return (
      <TextElement
        element={textEl}
        fill={fill}
        writing={writing}
        writeMs={writeMs}
        onWritingDone={onWritingDone}
        paused={paused}
      />
    );
  }

  if (!html) {
    return (
      <div
        style={fill ? { width: '100%', height: '100%' } : undefined}
        className="flex h-20 w-full flex-col items-center justify-center gap-1 rounded-md"
      >
        <span className="text-[12px] italic" style={{ color: 'rgba(60, 45, 25, 0.5)' }}>
          (latex)
        </span>
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      style={{
        fontSize,
        color,
        ...(ta != null ? { display: 'flex', width: '100%', alignItems: 'center', justifyContent: justify } : null),
        ...(fill ? { width: '100%', height: '100%' } : null),
      }}
      className={
        ta != null
          ? `katex-host overflow-x-auto leading-none ${fill ? 'h-full' : ''}`
          : `katex-host overflow-x-auto leading-none ${
              data.displayMode
                ? 'flex w-full items-center justify-center text-center'
                : 'inline-block max-w-full'
            } ${fill ? 'h-full' : ''}`
      }
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
